// Bridge between the user's staged corpus on Supabase Storage and the
// training backend's `dataset_uri`. Reads every file under the user's
// staging folder, normalizes them into a single JSONL bundle, uploads
// the bundle to a per-job folder in the same bucket, and mints a 24h
// signed URL the GPU worker downloads from.
//
// The staging folder is left intact so the user can iterate on their
// corpus across runs and across devices.

import { supabase } from '@/integrations/supabase/client';
import { CorpusStorage } from '@/services/CorpusStorage';

const BUCKET = 'training-corpora';
const SIGNED_URL_TTL_SECONDS = 24 * 60 * 60;

export interface CorpusBundleMeta {
  fileName: string;
  fileCount: number;
  byteCount: number;
}

export interface CorpusUploadResult {
  signedUrl: string;
  storagePath: string; // <user_id>/<request_id>/corpus.jsonl
  meta: CorpusBundleMeta;
}

function safeName(name: string) {
  return name.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
}

function classifyExt(name: string): 'jsonl' | 'json' | 'csv' | 'text' | 'unknown' {
  const ext = name.includes('.') ? name.split('.').pop()!.toLowerCase() : '';
  if (ext === 'jsonl') return 'jsonl';
  if (ext === 'json') return 'json';
  if (ext === 'csv') return 'csv';
  if (ext === 'txt' || ext === 'md' || ext === 'rst') return 'text';
  return 'unknown';
}

/**
 * Read every staged corpus file for this user and emit one normalized
 * JSONL line per record. The training worker's preprocessor will
 * re-split + re-shuffle, so we just need to produce one record per
 * logical unit.
 */
async function buildJsonlFromStaging(userId: string): Promise<{ blob: Blob; meta: CorpusBundleMeta }> {
  const files = await CorpusStorage.list(userId);

  if (files.length === 0) {
    throw new Error('Corpus is empty — upload at least one file first.');
  }

  const lines: string[] = [];
  let byteCount = 0;

  for (const entry of files) {
    let content: string;
    try {
      content = await CorpusStorage.readText(entry.path);
    } catch (e) {
      console.warn('CorpusUpload: skipping unreadable file', entry.name, e);
      continue;
    }
    if (!content) continue;
    byteCount += content.length;

    switch (classifyExt(entry.name)) {
      case 'jsonl': {
        for (const raw of content.split(/\r?\n/)) {
          const trimmed = raw.trim();
          if (!trimmed) continue;
          try {
            JSON.parse(trimmed);
            lines.push(trimmed);
          } catch {
            lines.push(JSON.stringify({ text: trimmed, source: safeName(entry.name) }));
          }
        }
        break;
      }
      case 'json': {
        try {
          const parsed = JSON.parse(content);
          if (Array.isArray(parsed)) {
            for (const item of parsed) {
              lines.push(JSON.stringify(item));
            }
          } else if (parsed && typeof parsed === 'object') {
            lines.push(JSON.stringify(parsed));
          }
        } catch {
          lines.push(JSON.stringify({ text: content, source: safeName(entry.name) }));
        }
        break;
      }
      case 'csv': {
        const rows = content.split(/\r?\n/);
        if (!rows.length) break;
        const headers = rows[0].split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
        for (let i = 1; i < rows.length; i++) {
          const cells = rows[i].split(',');
          if (cells.length !== headers.length) continue;
          const obj: Record<string, string> = {};
          headers.forEach((h, idx) => (obj[h] = (cells[idx] ?? '').trim().replace(/^"|"$/g, '')));
          lines.push(JSON.stringify(obj));
        }
        break;
      }
      case 'text':
      default:
        lines.push(JSON.stringify({ text: content, source: safeName(entry.name) }));
        break;
    }
  }

  if (lines.length === 0) {
    throw new Error('Corpus produced no usable records.');
  }

  const blob = new Blob([lines.join('\n') + '\n'], { type: 'application/x-jsonlines' });
  return {
    blob,
    meta: { fileName: 'corpus.jsonl', fileCount: files.length, byteCount },
  };
}

export const CorpusUploadService = {
  /**
   * Bundle the user's staged corpus into a single JSONL, drop it under
   * <user_id>/<requestId>/corpus.jsonl in the training-corpora bucket,
   * and mint a 24h signed URL for the GPU worker.
   */
  async bundleAndSign(opts: {
    userId: string;
    requestId: string;
  }): Promise<CorpusUploadResult> {
    const { userId, requestId } = opts;
    if (!userId) throw new Error('Not signed in.');

    const { blob, meta } = await buildJsonlFromStaging(userId);
    const storagePath = `${userId}/${requestId}/corpus.jsonl`;

    const { error: uploadError } = await supabase.storage
      .from(BUCKET)
      .upload(storagePath, blob, {
        contentType: 'application/x-jsonlines',
        upsert: true,
      });
    if (uploadError) {
      throw new Error(`Corpus upload failed: ${uploadError.message}`);
    }

    const { data: signed, error: signError } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(storagePath, SIGNED_URL_TTL_SECONDS);
    if (signError || !signed?.signedUrl) {
      throw new Error(`Could not sign corpus URL: ${signError?.message ?? 'unknown'}`);
    }

    return {
      signedUrl: signed.signedUrl,
      storagePath,
      meta,
    };
  },
};
